import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { TablePaginationConfig } from "antd5";
import { FilterValue, SorterResult } from "antd5/es/table/interface";
import { startCase } from "lodash";
import isEqual from "lodash/isEqual";

import { RouterTextLink } from "components/actions/RouterLink";
import { TableCell } from "components/table_components/TableCellStyles";
import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem } from "components/ui/dropdown_menu";
import DateInput from "components/ui/input/DateInput";
import toast from "components/ui/toast/Toast";
import { ColumnType } from "lib/core_components/Table/ColumnTypes";
import { Table, tableRowSelection } from "lib/core_components/Table/Table";
import { createUseDebounce } from "lib/debounce";
import { BidResponseDto } from "lib/hooks/api/bid/bid_responses/useBidResponses";
import { useUpdateBidResponses } from "lib/hooks/api/bid/bid_responses/useUpdateBidResponses";
import { useFormAutoSubmit } from "lib/hooks/useFormAutoSubmit";
import { BidResponseSortBy, BidResponseSortOrder } from "lib/types/graphQLEnums";
import { BidResponsesDto } from "../../../lib/hooks/api/bid/bid_responses";
import { EventNames, useTracking } from "../../../lib/tracking";
import { Flex, Text } from "../../../styles/utility-components";
import { CompanyUserCombobox } from "../../ui/form_components/CompanyUserCombobox";
import { BidResponseStageSelect } from "../../ui/form_components/StageSelect/BidResponseStageSelect";
import { BidResponseDrawer } from "../bid_response_drawer/BidResponseDrawer";
import { AnswerSource } from "../types";
import DeleteBidResponsesModal from "./modals/DeleteBidResponseModal";
import {
  BidResponsesFilters,
  BidResponseSortState,
  DueDateStatus,
  getBidResponseRoute,
  getBidRoute,
  getDueDateStatus,
  mapSortFieldToEnum,
  toDueDateIso,
} from "./utils";

const useDebounce500 = createUseDebounce(500);

// Listed in the order the columns are defined below, which is the order they render in.
const CROSS_BID_COLUMN_KEYS = [
  "bid",
  "title",
  "stage",
  "assignee",
  "reviewer",
  "dueDate",
  "actions",
];

function getSortOrder(
  sort: BidResponseSortState,
  key: BidResponseSortBy,
): "ascend" | "descend" | null {
  if (!sort || sort.field !== key) {
    return null;
  }
  return sort.order === BidResponseSortOrder.Asc ? "ascend" : "descend";
}

// --- Inline editable cell components ---

function InlineStageCell({ bidResponse }: { bidResponse: BidResponseDto }) {
  const { mutate } = useUpdateBidResponses({
    onSuccess: () => toast.success("Status updated successfully"),
    onError: () => toast.error("Failed to update bid response"),
  });

  return (
    <BidResponseStageSelect
      value={bidResponse.stageId}
      onValueChange={(stageId) => mutate({ input: { ids: [bidResponse.id], stageId } })}
    />
  );
}

function InlineUserCell({
  bidResponse,
  field,
}: {
  bidResponse: BidResponseDto;
  field: "assigneeId" | "reviewerId";
}) {
  const value = field === "assigneeId" ? bidResponse.assignee?.guid : bidResponse.reviewer?.guid;
  const label = field === "assigneeId" ? "Assignee" : "Reviewer";
  const { mutate } = useUpdateBidResponses({
    onSuccess: () => toast.success(`${label} updated successfully`),
    onError: () => toast.error("Failed to update bid response"),
  });

  return (
    <CompanyUserCombobox
      value={value}
      truncate
      onChange={(newValue) => mutate({ input: { ids: [bidResponse.id], [field]: newValue } })}
    />
  );
}

type DueDateFormValues = { dueDate: Date | string | null };

function InlineDueDateCell({ bidResponse }: { bidResponse: BidResponseDto }) {
  const { mutate } = useUpdateBidResponses({
    onSuccess: () => toast.success("Due date updated successfully"),
    onError: () => toast.error("Failed to update bid response"),
  });

  const { control, watch, getValues, trigger, reset } = useForm<DueDateFormValues>({
    defaultValues: { dueDate: bidResponse.dueDate ?? null },
  });

  useEffect(() => {
    reset({ dueDate: bidResponse.dueDate ?? null });
  }, [bidResponse.dueDate, reset]);

  // Debounced because DateInput's text field emits a change on every keystroke that
  // parses, so typing a date would otherwise fire several mutations.
  const handleAutoSubmit = useDebounce500((values: DueDateFormValues) => {
    const dueDate = toDueDateIso(values.dueDate);
    // Both sides normalised, since the cached value may be a Date or an ISO string.
    if (dueDate === toDueDateIso(bidResponse.dueDate)) return;
    mutate({ input: { ids: [bidResponse.id], dueDate } });
  });

  useFormAutoSubmit({ watch, getValues, trigger, onChange: handleAutoSubmit });

  return (
    <DueDateWrapper status={getDueDateStatus(bidResponse.dueDate)}>
      <DateInput
        name="dueDate"
        control={control}
        calendarProps={{ mode: "single" }}
        triggerWidth="100%"
        aria-label="Due date"
      />
    </DueDateWrapper>
  );
}

// Amber when due within DUE_SOON_DAYS, red when overdue. Scoped to the first child (the
// trigger) because DateInput's popover isn't portalled — `& button` would hit the calendar.
const DueDateWrapper = styled.div<{ status: DueDateStatus }>(({ theme, status }) => {
  const color =
    status === "overdue"
      ? theme.colors.sysDestructiveDefault
      : status === "dueSoon"
        ? theme.colors.sysWarningDefault
        : undefined;

  return {
    width: "100%",
    ...(color ? { "& > *:first-of-type button": { color } } : {}),
  };
});

function getBidResponseTableColumns({
  bidId,
  sortState,
  setSelectedSource,
}: {
  sortState: BidResponseSortState;
  bidId: string | undefined;
  setSelectedSource: (source: AnswerSource | null) => void;
}): ColumnType<BidResponseDto>[] {
  const columns: ColumnType<BidResponseDto>[] = [
    {
      key: "bid",
      title: "Bid",
      render: (_, response) => (
        <TableCell minWidth={160} overflow="hidden">
          {response.bid ? (
            <RouterTextLink to={getBidRoute(response.bid.id)}>{response.bid.name}</RouterTextLink>
          ) : (
            <Text>-</Text>
          )}
        </TableCell>
      ),
      ellipsis: { showTitle: false },
      width: 160,
    },
    {
      key: "section",
      title: "Section",
      render: (_, response) => (
        <TableCell minWidth={100}>
          <Text>{response.section || "-"}</Text>
        </TableCell>
      ),
      width: 100,
      sorter: true,
      sortOrder: getSortOrder(sortState, BidResponseSortBy.Section),
    },
    {
      key: "reference",
      title: "Reference",
      sorter: true,
      sortOrder: getSortOrder(sortState, BidResponseSortBy.Reference),
      render: (_, response) => (
        <TableCell minWidth={100}>
          <Text>{response.reference || "-"}</Text>
        </TableCell>
      ),
      width: 100,
    },
    {
      key: "title",
      title: "Response title",
      sorter: true,
      sortOrder: getSortOrder(sortState, BidResponseSortBy.Title),
      sortDirections: ["ascend", "descend", null],
      render: (_, response) => (
        <TableCell overflow="hidden">
          <RouterTextLink to={getBidResponseRoute(bidId, response.id)}>
            {response.title}
          </RouterTextLink>
        </TableCell>
      ),
      ellipsis: { showTitle: false },
      sizeConfig: {
        xlarge: 200,
        large: 200,
        medium: 200,
        small: 160,
      },
      width: 200,
    },
    {
      key: "description",
      title: "Question",
      // Deliberately no `ellipsis` here, so the question text wraps over several lines
      // instead of being clipped to one.
      render: (_, response) => (
        <TableCell>
          <Text>{response.description || "-"}</Text>
        </TableCell>
      ),
      sizeConfig: {
        xlarge: 390,
        large: 390,
        medium: 350,
        small: 280,
      },
      width: 390,
    },
    {
      key: "source",
      title: "Source",
      render: (_, response) => (
        <TableCell overflow="hidden">
          {response.source && (
            <Text
              color="sysPrimaryDefault"
              as="a"
              ellipsis
              onClick={() => {
                if (response.source) {
                  setSelectedSource({
                    ...response.source,
                    fileName: response.source.title,
                  });
                }
              }}
            >
              {response.source.title}
            </Text>
          )}
        </TableCell>
      ),
      ellipsis: { showTitle: false },
      sizeConfig: {
        xlarge: 150,
        large: 150,
        medium: 150,
        small: 130,
      },
      width: 150,
    },
    {
      key: "stage",
      title: "Status",
      render: (_, response) => (
        <TableCell minWidth={140}>
          <InlineStageCell bidResponse={response} />
        </TableCell>
      ),
      sorter: true,
      sortOrder: getSortOrder(sortState, BidResponseSortBy.Stage),
      sortDirections: ["ascend", "descend", null],
      width: 140,
    },
    {
      key: "assignee",
      title: "Assigned to",
      render: (_, response) => (
        <TableCell minWidth={180} overflow="hidden">
          <InlineUserCell bidResponse={response} field="assigneeId" />
        </TableCell>
      ),
      ellipsis: { showTitle: false },
      width: 180,
    },
    {
      key: "reviewer",
      title: "Reviewer",
      render: (_, response) => (
        <TableCell minWidth={180} overflow="hidden">
          <InlineUserCell bidResponse={response} field="reviewerId" />
        </TableCell>
      ),
      ellipsis: { showTitle: false },
      width: 180,
    },
    {
      key: "dueDate",
      title: "Due date",
      render: (_, response) => (
        <TableCell minWidth={160}>
          <InlineDueDateCell bidResponse={response} />
        </TableCell>
      ),
      sorter: true,
      sortOrder: getSortOrder(sortState, BidResponseSortBy.DueDate),
      sortDirections: ["ascend", "descend", null],
      width: 160,
    },
    {
      key: "weighting",
      title: "Weighting",
      render: (_, response) => (
        <TableCell minWidth={100}>
          <Text>{response.weighting || "-"}</Text>
        </TableCell>
      ),
      width: 100,
    },
    {
      key: "formattingRequirements",
      title: "Format",
      render: (_, response) => (
        <TableCell minWidth={100}>
          <Text>{response.formattingRequirements || "-"}</Text>
        </TableCell>
      ),
      width: 100,
    },
    {
      key: "actions",
      title: "",
      render: (_, bidResponse) => (
        <TableCell minWidth={60}>
          <Flex alignItems="center" justifyContent="center">
            <ActionColumn bidResponse={bidResponse} />
          </Flex>
        </TableCell>
      ),
      width: 60,
    },
  ];

  // Across bids the table is a task list, so it carries only what someone triaging needs.
  // Within a bid it keeps every column, minus the bid it is already scoped to.
  return bidId
    ? columns.filter((column) => column.key !== "bid")
    : columns.filter((column) => CROSS_BID_COLUMN_KEYS.includes(String(column.key)));
}

type Props = {
  bidResponses: BidResponseDto[];
  isLoading: boolean;
  isError: boolean;
  onSortChange: (sortState: BidResponseSortState) => void;

  bidId: string | undefined;
  selectedKeys: string[];
  filters: BidResponsesFilters;
  pagination: TablePaginationConfig;
  setSelectedKeys: (keys: string[]) => void;
  setSelectedSource: (source: AnswerSource | null) => void;
};

export default function BidResponsesTable({
  bidResponses,
  isLoading,
  isError,
  bidId,
  selectedKeys,
  filters,
  pagination,
  onSortChange,
  setSelectedKeys,
  setSelectedSource,
}: Props) {
  const { logEvent } = useTracking();
  const sortState = useMemo<BidResponseSortState>(
    () => ({
      order: filters.order,
      field: filters.orderBy,
    }),
    [filters.order, filters.orderBy],
  );

  const columns = useMemo(
    (): ColumnType<BidResponseDto>[] =>
      getBidResponseTableColumns({
        bidId,
        sortState,
        setSelectedSource,
      }),
    [bidId, sortState, setSelectedSource],
  );

  const handleTableChange = (
    _: TablePaginationConfig,
    __: Record<string, FilterValue | null>,
    sorter: SorterResult<BidResponsesDto> | SorterResult<BidResponsesDto>[],
  ): void => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (!sortState && !currentSorter.order) {
      return;
    }

    const newSortState: BidResponseSortState = {
      order:
        currentSorter.order === "descend" ? BidResponseSortOrder.Desc : BidResponseSortOrder.Asc,
      field: mapSortFieldToEnum(currentSorter.columnKey?.toString() || ""),
    };

    if (!isEqual(sortState, newSortState)) {
      onSortChange(newSortState);
      logEvent(EventNames.tableSorted, {
        Context: "Column action",
        "Sorted by": startCase(newSortState.field),
        "Sort order": newSortState.order,
      });
    }
  };

  return (
    <Table
      dataSource={bidResponses}
      columns={columns}
      loading={isLoading}
      isError={isError}
      rowKey="id"
      rowSelection={tableRowSelection(selectedKeys, setSelectedKeys, bidResponses, "id")}
      pagination={pagination}
      scroll={{ x: "100%" }}
      style={{ width: "100%" }}
      onChange={handleTableChange}
    />
  );
}

type ActionColumnProps = {
  bidResponse: BidResponseDto;
};

function ActionColumn({ bidResponse }: ActionColumnProps) {
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

  return (
    <>
      <DropdownMenu
        trigger={
          <TriggerButton
            icon="dotsVertical"
            buttonType="tertiary"
            iconProps={{ color: "sysTextDefault" }}
          />
        }
        width="160px"
        align="end"
      >
        <DropdownMenuItem
          key="edit"
          icon="edit02"
          onClick={() => setIsEditDrawerOpen(true)}
          aria-label="edit"
        >
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          key="delete"
          aria-label="Delete"
          icon="trash03"
          onClick={() => setDeleteModalOpen(true)}
        >
          Delete
        </DropdownMenuItem>
      </DropdownMenu>
      <DeleteBidResponsesModal
        isOpen={isDeleteModalOpen}
        ids={[bidResponse.id]}
        onOpenChange={(open) => {
          setDeleteModalOpen(open);
        }}
      />
      <BidResponseDrawer
        type="edit"
        isOpen={isEditDrawerOpen}
        onClose={() => setIsEditDrawerOpen(false)}
        onComplete={() => setIsEditDrawerOpen(false)}
        defaultValues={{
          ...bidResponse,
          reference: bidResponse.reference || undefined,
          weighting: bidResponse.weighting || undefined,
          formattingRequirements: bidResponse.formattingRequirements || undefined,
          section: bidResponse.section || undefined,
          description: bidResponse.description || undefined,
          dueDate: bidResponse.dueDate || undefined,

          bidId: bidResponse.bidId ?? undefined,
          assigneeId: bidResponse.assignee?.guid || undefined,
          reviewerId: bidResponse.reviewer?.guid || undefined,
        }}
      />
    </>
  );
}

const TriggerButton = styled(Button)(({ theme }) => ({
  background: "transparent",
  "&:hover": {
    background: theme.colors.sysBackgroundInactive,
  },
  "&:active": {
    background: theme.colors.sysBackgroundInactive,
  },
  "&:focus-visible": {
    background: theme.colors.sysBackgroundInactive,
  },
}));
